import React, { useState, useEffect, useCallback, Fragment } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Checkbox, FormControlLabel, FormGroup, Grid, Divider, Collapse,
  AppBar, Toolbar, Tabs, Tab, Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { resolveMediaUrl } from '../../context/CompanyContext';

const API = (process.env.REACT_APP_API_URL || 'https://hr.adityatrading.in/').replace(/\/$/, '');

// Same rule as the backend's clean_company_domain: a bare hostname only — no
// scheme, path, port, '@' or spaces. Blank is allowed (code-only company).
const DOMAIN_RE = /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;
const domainError = (d) => (d && !DOMAIN_RE.test(d) ? 'Enter a plain domain like payroll.example.com' : '');

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('superadmin_token')}` };
}

function CompanyExpandedRow({ company, allModules, onRefresh }) {
  const [tab, setTab] = useState(0);
  const [moduleState, setModuleState] = useState({});
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ user_name: '', password: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editFields, setEditFields] = useState({
    name: company.name, short_name: company.short_name || '', legal_name: company.legal_name || '',
    address: company.address || '', registration_number: company.registration_number || '',
    domain: company.domain || '', primary_color: company.primary_color || '#1976d2',
  });
  const [copyMsg, setCopyMsg] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  const toggleShowPassword = (id) => {
    setShowPasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    axios.get(`${API}/superadmin/companies/${company.code}/modules/`, { headers: getHeaders() })
      .then((res) => {
        const s = {};
        res.data.modules.forEach((m) => { s[m.id] = m.is_enabled; });
        setModuleState(s);
      }).catch(() => {});

    axios.get(`${API}/superadmin/companies/${company.code}/admins/`, { headers: getHeaders() })
      .then((res) => setAdmins(res.data.admins))
      .catch(() => {});
  }, [company.code]);

  const portalUrl = company.domain
    ? `https://${company.domain}/home`
    : `${window.location.origin}/${company.code.toLowerCase()}/home`;

  const copyUrl = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopyMsg(true);
    setTimeout(() => setCopyMsg(false), 2000);
  };

  const saveCompanyDetails = async () => {
    if (domainError(editFields.domain)) return;
    // Send only what changed (domain in particular), not the whole form.
    const original = {
      name: company.name, short_name: company.short_name || '', legal_name: company.legal_name || '',
      address: company.address || '', registration_number: company.registration_number || '',
      domain: company.domain || '', primary_color: company.primary_color || '#1976d2',
    };
    const changes = Object.fromEntries(Object.entries(editFields).filter(([k, v]) => v !== original[k]));
    if (!Object.keys(changes).length) return;
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/companies/${company.code}/`, changes, { headers: getHeaders() });
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async () => {
    setSaving(true);
    const enabled = Object.entries(moduleState).filter(([, v]) => v).map(([k]) => k);
    try {
      await axios.put(`${API}/superadmin/companies/${company.code}/modules/`, { modules: enabled }, { headers: getHeaders() });
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save modules');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async () => {
    setFormError('');
    try {
      await axios.post(`${API}/superadmin/companies/${company.code}/admins/`, newAdmin, { headers: getHeaders() });
      setNewAdmin({ user_name: '', password: '' });
      const res = await axios.get(`${API}/superadmin/companies/${company.code}/admins/`, { headers: getHeaders() });
      setAdmins(res.data.admins);
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    try {
      await axios.delete(`${API}/superadmin/companies/${company.code}/admins/`, {
        headers: getHeaders(),
        data: { admin_id: adminId },
      });
      const res = await axios.get(`${API}/superadmin/companies/${company.code}/admins/`, { headers: getHeaders() });
      setAdmins(res.data.admins);
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to delete admin');
    }
  };

  const handleLogoUpload = async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await axios.post(`${API}/superadmin/companies/${company.code}/logo/`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to upload logo');
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc' }}>
      {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
        <Tab label="Company Info" />
        <Tab label="Modules" />
        <Tab label="HR Admins" />
      </Tabs>

      {/* ── Tab 0: Company Info ── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
              <Avatar
                src={resolveMediaUrl(company.logo, API)}
                sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: company.primary_color || '#1976d2', fontSize: '1.8rem' }}
              >
                {company.short_name?.charAt(0) || company.code?.charAt(0)}
              </Avatar>
              <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} size="small" sx={{ borderRadius: 2 }}>
                Upload Logo
                <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files[0]) handleLogoUpload(e.target.files[0]); }} />
              </Button>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.8rem' }}>
                  {portalUrl}
                </Typography>
                <IconButton size="small" onClick={copyUrl}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => window.open(portalUrl, '_blank')}><OpenInNewIcon sx={{ fontSize: 14 }} /></IconButton>
              </Box>
              {copyMsg && <Typography variant="caption" sx={{ color: '#22c55e' }}>Copied!</Typography>}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>Company Details</Typography>
              <TextField fullWidth label="Company Name" value={editFields.name}
                onChange={(e) => setEditFields({ ...editFields, name: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Short Name" value={editFields.short_name}
                onChange={(e) => setEditFields({ ...editFields, short_name: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Legal Name (Payslip)" value={editFields.legal_name}
                onChange={(e) => setEditFields({ ...editFields, legal_name: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth multiline minRows={2} label="Address (Payslip)" value={editFields.address}
                onChange={(e) => setEditFields({ ...editFields, address: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Registration Number / CIN (Payslip)" value={editFields.registration_number}
                onChange={(e) => setEditFields({ ...editFields, registration_number: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Domain" placeholder="e.g. payroll.capitalbridge.in" value={editFields.domain}
                onChange={(e) => setEditFields({ ...editFields, domain: e.target.value.toLowerCase().trim() })} sx={{ mb: 2 }}
                error={!!domainError(editFields.domain)}
                helperText={domainError(editFields.domain) || "Custom domain for this company's portal (optional)"} />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField label="Primary Color" type="color" value={editFields.primary_color}
                  onChange={(e) => setEditFields({ ...editFields, primary_color: e.target.value })}
                  InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: editFields.primary_color, border: '1px solid #e2e8f0' }} />
              </Box>
              <Button variant="contained" onClick={saveCompanyDetails} disabled={saving} sx={{ borderRadius: 2, fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>HR Admin Credentials</Typography>
              {admins.length > 0 ? (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Password Reset</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {admins.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.user_name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {showPasswords[a.id] ? (a.plain_password || '—') : '••••••••'}
                            </Typography>
                            <IconButton size="small" onClick={() => toggleShowPassword(a.id)}>
                              {showPasswords[a.id] ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={a.must_reset_password ? 'Pending first login' : 'Completed'} size="small"
                            color={a.must_reset_password ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleDeleteAdmin(a.id)}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>No admins created yet.</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#475569' }}>Add New Admin</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField label="Username" value={newAdmin.user_name} size="small"
                  onChange={(e) => setNewAdmin({ ...newAdmin, user_name: e.target.value })} sx={{ minWidth: 160 }} />
                <TextField label="Password" type="password" value={newAdmin.password} size="small"
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} sx={{ minWidth: 160 }} />
                <Button variant="contained" onClick={handleCreateAdmin} size="medium"
                  disabled={!newAdmin.user_name || !newAdmin.password} sx={{ borderRadius: 2, fontWeight: 600, height: 40 }}>
                  Create
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>
                The admin will be prompted to change their password on first login.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Modules ── */}
      {tab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>Module Access Control</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Enable or disable modules for this company. Disabled modules will be hidden from the HR portal.
          </Typography>
          <FormGroup>
            <Grid container spacing={1}>
              {allModules.map((m) => (
                <Grid item xs={6} sm={4} md={3} key={m.id}>
                  <FormControlLabel
                    control={<Checkbox checked={!!moduleState[m.id]} onChange={(e) => setModuleState({ ...moduleState, [m.id]: e.target.checked })} />}
                    label={<Typography variant="body2">{m.label}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={saveModules} disabled={saving} sx={{ borderRadius: 2, fontWeight: 600 }}>
              {saving ? 'Saving...' : 'Save Modules'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Tab 2: Admins ── */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
                Existing HR Admins
              </Typography>
              {admins.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No admins yet.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Password Reset</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {admins.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.user_name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {showPasswords[a.id] ? (a.plain_password || '—') : '••••••••'}
                            </Typography>
                            <IconButton size="small" onClick={() => toggleShowPassword(a.id)}>
                              {showPasswords[a.id] ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={a.must_reset_password ? 'Pending' : 'Done'} size="small"
                            color={a.must_reset_password ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleDeleteAdmin(a.id)}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>Create New Admin</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                The admin will be prompted to change their password on first login.
              </Typography>
              <TextField fullWidth label="Username" value={newAdmin.user_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, user_name: e.target.value })} sx={{ mb: 2 }} size="small" />
              <TextField fullWidth label="Initial Password" type="password" value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} sx={{ mb: 2 }} size="small" />
              <Button variant="contained" onClick={handleCreateAdmin}
                disabled={!newAdmin.user_name || !newAdmin.password} fullWidth sx={{ borderRadius: 2, fontWeight: 600 }}>
                Create Admin
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default function SuperAdminDashboard() {
  const [companies, setCompanies] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);

  const [newCompany, setNewCompany] = useState({
    code: '', name: '', short_name: '', domain: '', primary_color: '#1976d2',
  });
  const [formError, setFormError] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/companies/`, { headers: getHeaders() });
      setCompanies(res.data.companies);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('superadmin_token');
        window.location.href = '/superadmin/login';
      }
      setError(err.response?.data?.error || 'Failed to load companies');
    }
  }, []);

  const fetchModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/modules/`, { headers: getHeaders() });
      setAllModules(res.data.modules);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('superadmin_token')) {
      window.location.href = '/superadmin/login';
      return;
    }
    fetchCompanies();
    fetchModules();
  }, [fetchCompanies, fetchModules]);

  const handleCreateCompany = async () => {
    setFormError('');
    if (domainError(newCompany.domain)) { setFormError(domainError(newCompany.domain)); return; }
    try {
      const res = await axios.post(`${API}/superadmin/companies/`, newCompany, { headers: getHeaders() });
      setCreateOpen(false);
      setNewCompany({ code: '', name: '', short_name: '', domain: '', primary_color: '#1976d2' });
      fetchCompanies();
      setExpandedCode(res.data.code);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create company');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('superadmin_user');
    window.location.href = '/superadmin/login';
  };

  const toggleExpand = (code) => {
    setExpandedCode((prev) => (prev === code ? null : code));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <AppBar position="static" sx={{ bgcolor: '#0f172a' }}>
        <Toolbar>
          <AdminPanelSettingsIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Super Admin Console
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, color: '#94a3b8' }}>
            {localStorage.getItem('superadmin_user')}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Companies
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2, fontWeight: 600 }}>
            New Company
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, width: 40 }} />
                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Company Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Portal URL</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Modules</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Admins</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((c) => (
                <Fragment key={c.id}>
                  <TableRow
                    hover
                    onClick={() => toggleExpand(c.code)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedCode === c.code ? 'none' : undefined } }}
                  >
                    <TableCell sx={{ width: 40, pr: 0 }}>
                      <IconButton size="small">
                        {expandedCode === c.code ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={resolveMediaUrl(c.logo, API)}
                          sx={{ width: 36, height: 36, bgcolor: c.primary_color || '#1976d2', fontSize: '0.8rem' }}
                        >
                          {c.short_name?.charAt(0) || c.code?.charAt(0)}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, color: '#1976d2' }}>{c.code}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {c.domain ? `https://${c.domain}/home` : `${window.location.origin}/${c.code.toLowerCase()}/home`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${c.enabled_modules?.length || 0} enabled`} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={c.admin_count || 0} size="small" color="secondary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={c.is_active ? 'Active' : 'Inactive'} size="small" color={c.is_active ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, px: 0, border: 'none' }}>
                      <Collapse in={expandedCode === c.code} timeout="auto" unmountOnExit>
                        <CompanyExpandedRow company={c} allModules={allModules} onRefresh={fetchCompanies} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                    No companies yet. Click "New Company" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create Company Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New Company</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField fullWidth label="Company Code" placeholder="e.g. CB001"
            value={newCompany.code} onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value.toUpperCase() })}
            sx={{ mt: 1, mb: 2 }} required helperText="Unique identifier (uppercase letters + numbers)" />
          <TextField fullWidth label="Company Name" placeholder="e.g. Cloud Bridge Solutions"
            value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
            sx={{ mb: 2 }} required />
          <TextField fullWidth label="Short Name" placeholder="e.g. CB"
            value={newCompany.short_name} onChange={(e) => setNewCompany({ ...newCompany, short_name: e.target.value })}
            sx={{ mb: 2 }} />
          <TextField fullWidth label="Domain" placeholder="e.g. payroll.capitalbridge.in"
            value={newCompany.domain} onChange={(e) => setNewCompany({ ...newCompany, domain: e.target.value.toLowerCase().trim() })}
            sx={{ mb: 2 }} error={!!domainError(newCompany.domain)}
            helperText={domainError(newCompany.domain) || 'Custom domain for the portal (optional)'} />
          <TextField fullWidth label="Primary Color" type="color"
            value={newCompany.primary_color} onChange={(e) => setNewCompany({ ...newCompany, primary_color: e.target.value })}
            sx={{ mb: 1 }} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompany}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
